import React from 'react';
import { useDateFieldState } from 'react-stately';
import type { DateFieldState, DateSegment } from 'react-stately';
import {
    useDateField,
    useDateSegment,
    useLocale,
} from 'react-aria';
import { createCalendar, DateValue } from '@internationalized/date';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AriaDateFieldOptions } from '@react-aria/datepicker';
import { borderRadius } from '../tokens';
import { colors } from '../colors';

const DateSegment = ({ segment, state }: { segment: DateSegment, state: DateFieldState }): JSX.Element => {
    const ref = React.useRef<HTMLDivElement>(null);
    const { segmentProps } = useDateSegment(segment, state, ref);

    return (
        <div
            {...segmentProps}
            ref={ref}
            css={{
                outline: 'none',
                textAlign: 'end',
                fontVariantNumeric: 'tabular-nums',
                boxSizing: 'content-box',
                minWidth: segment.maxValue != null ? `${String(segment.maxValue).length}ch` : undefined,
                padding: '1px',
                borderRadius: borderRadius.small,
                '&[aria-label="year"]': {
                    textAlign: 'start',
                },
                color: (() => {
                    if (segment.isPlaceholder) {
                        return colors.grey500;
                    }
                    if (segment.isEditable) {
                        return colors.grey600;
                    }
                    return colors.shadesBlack;
                })(),
                '&:focus': {
                    background: colors.statesFocus,
                    color: colors.shadesWhite,
                },
            }}
        >
            {segment.text}
        </div>
    );
};

const DateField = (props: AriaDateFieldOptions<DateValue>): JSX.Element => {
    const { locale } = useLocale();
    const state = useDateFieldState({
        ...props,
        locale,
        createCalendar,
    });

    const ref = React.useRef<HTMLDivElement>(null);
    const { fieldProps } = useDateField(props, state, ref);

    return (
        <div
            {...fieldProps}
            ref={ref}
            css={{
                display: 'flex',
                padding: 11,
                width: '100%',
            }}
        >
            {state.segments.map((segment, key) => (
                // No fully unique fields exist in the hook - need to use index as key
                // eslint-disable-next-line react/no-array-index-key
                <DateSegment key={key} segment={segment} state={state} />
            ))}
        </div>
    );
};

export default DateField;
